/**
 * /settings/imports-quickbooks — QuickBooks → Ledger Core import wizard.
 *
 * 7 steps:
 *   1. Connect       — QBO OAuth button / IIF upload / CSV upload
 *   2. Extract       — progress list (auto-advances)
 *   3. Auto-map      — summary counters (auto-advances)
 *   4. Interview     — one question at a time
 *   5. Preview       — write plan summary
 *   6. Apply         — progress bar (polled)
 *   7. Reconciliation — TB diff result
 *
 * State: jobId in local state. Job status drives which step is shown.
 * Resume: on mount, check for any active job → restore step.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  LinearProgress,
  Radio,
  RadioGroup,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import LinkIcon from "@mui/icons-material/Link";
import RefreshIcon from "@mui/icons-material/Refresh";
import { FileDropZone } from "@/components/FileDropZone";
import { api, errorMessage } from "@/lib/api";

export const Route = createFileRoute("/_auth/settings/imports-quickbooks")({
  component: QuickBooksImportPage,
});

// ── Types ─────────────────────────────────────────────────────────────────────

type QboConnection = {
  entity_id: string;
  realm_id: string;
  company_name: string | null;
  token_expires_at: string | null;
};

type ImportJob = {
  id: string;
  source_kind: string;
  status: string;
  cutover_date: string | null;
  records_extracted: number | null;
  records_auto_mapped: number | null;
  records_questioned: number | null;
  records_applied: number | null;
  created_at: string;
  completed_at: string | null;
};

type InterviewTurn = {
  id: string;
  turn_no: number;
  template_id: string;
  question_text: string;
  options: { value: string; label: string; action_descr: string }[];
  selected_value: string | null;
  answered_at: string | null;
};

type PlanPreview = {
  job_id: string;
  total_steps: number;
  by_kind: Record<string, number>;
  sample_steps: { step: number; kind: string; source_record_kind: string }[];
};

type Reconciliation = {
  status: string;
  reason?: string;
  accounts_matched?: number;
  mismatches?: { account: string; qbo_balance: string; lc_balance: string; delta: string }[];
  lc_account_count?: number;
  lc_total_debits?: string;
  lc_total_credits?: string;
};

// ── Status → step mapping ─────────────────────────────────────────────────────

function jobStatusToStep(status: string): number {
  switch (status) {
    case "extracting": return 2;
    case "resolving":  return 3;
    case "interviewing": return 4;
    case "planning":   return 5;
    case "applying":   return 6;
    case "reconciling": return 6;
    case "done":       return 7;
    case "failed":     return -1;
    default:           return 1;
  }
}

const STEP_LABELS = [
  "Connect",
  "Extract",
  "Auto-map",
  "Interview",
  "Preview",
  "Apply",
  "Done",
];

// ── Main page ─────────────────────────────────────────────────────────────────

function QuickBooksImportPage() {
  const qc = useQueryClient();
  const [step, setStep] = useState(1);
  const [jobId, setJobId] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // ── QBO connection ──────────────────────────────────────────────────────────
  const { data: qboConn, isLoading: connLoading } = useQuery<QboConnection | null>({
    queryKey: ["qbo-connection"],
    queryFn: async () => {
      const r = await api.raw("/api/imports/quickbooks/connection", {
        ignoreResponseError: true,
      });
      return r.status === 204 || r.status === 404
        ? null
        : (r._data as QboConnection);
    },
    retry: false,
  });

  // ── Job list (for resume) ──────────────────────────────────────────────────
  const { data: jobList } = useQuery<ImportJob[]>({
    queryKey: ["qbo-jobs"],
    queryFn: () => api("/api/imports/quickbooks/jobs"),
    retry: false,
  });

  // On mount: look for an active job to resume
  useEffect(() => {
    if (!jobList) return;
    const active = jobList.find((j) =>
      !["done", "failed"].includes(j.status)
    );
    if (active && !jobId) {
      setJobId(active.id);
      setStep(jobStatusToStep(active.status));
    }
    // Also restore from URL param (after OAuth callback)
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected") === "1" && !jobId) {
      setStep(1); // stay on connect, connection will appear
    }
  }, [jobList, jobId]);

  // ── Job polling ────────────────────────────────────────────────────────────
  const { data: currentJob } = useQuery<ImportJob>({
    queryKey: ["qbo-job", jobId],
    queryFn: () => api(`/api/imports/quickbooks/jobs/${jobId}`),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (!status) return false;
      if (["extracting", "resolving", "applying", "reconciling"].includes(status)) return 2000;
      return false;
    },
  });

  // Advance step when job status changes
  useEffect(() => {
    if (!currentJob) return;
    const newStep = jobStatusToStep(currentJob.status);
    if (newStep > 0) setStep(newStep);
  }, [currentJob?.status]);

  // ── Create job (QBO) ───────────────────────────────────────────────────────
  const createQboJob = useMutation({
    mutationFn: () =>
      api<ImportJob>("/api/imports/quickbooks/jobs", {
        method: "POST",
        query: { source_kind: "qbo" },
      }),
    onSuccess: (job) => {
      setJobId(job.id);
      setStep(2);
      qc.invalidateQueries({ queryKey: ["qbo-jobs"] });
    },
    onError: (e) => setErrorMsg(errorMessage(e)),
  });

  // ── Upload IIF/CSV ─────────────────────────────────────────────────────────
  const uploadFile = useMutation({
    mutationFn: async ({ file, kind }: { file: File; kind: "iif" | "csv" }) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("source_kind", kind);
      return await api<ImportJob>(`/api/imports/quickbooks/jobs?source_kind=${kind}`, {
        method: "POST",
        body: fd,
      });
    },
    onSuccess: (job) => {
      setJobId(job.id);
      setStep(2);
      qc.invalidateQueries({ queryKey: ["qbo-jobs"] });
    },
    onError: (e) => setErrorMsg(errorMessage(e)),
  });

  const handleApply = useMutation({
    mutationFn: () =>
      api(`/api/imports/quickbooks/jobs/${jobId}/apply`, { method: "POST" }),
    onSuccess: () => {
      setStep(6);
      qc.invalidateQueries({ queryKey: ["qbo-job", jobId] });
    },
    onError: (e) => setErrorMsg(errorMessage(e)),
  });

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ maxWidth: 820, mx: "auto", py: 4, px: 2 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        QuickBooks Import
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Migrate your Chart of Accounts, customers, vendors, and transaction history
        from QuickBooks to Ledger Core in about 30 minutes.
      </Typography>

      {/* Stepper */}
      <Stepper activeStep={step - 1} sx={{ mb: 4 }} alternativeLabel>
        {STEP_LABELS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {errorMsg && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMsg(null)}>
          {errorMsg}
        </Alert>
      )}

      {/* Step panels */}
      {step === 1 && (
        <StepConnect
          qboConn={qboConn ?? null}
          connLoading={connLoading}
          onConnectQbo={() => {
            window.location.href = "/api/imports/quickbooks/connect";
          }}
          onStartQboImport={() => createQboJob.mutate()}
          starting={createQboJob.isPending || uploadFile.isPending}
          onIifFile={(f) => uploadFile.mutate({ file: f, kind: "iif" })}
          onCsvFile={(f) => uploadFile.mutate({ file: f, kind: "csv" })}
        />
      )}
      {step === 2 && currentJob && (
        <StepExtract job={currentJob} />
      )}
      {step === 3 && currentJob && (
        <StepAutoMap job={currentJob} />
      )}
      {step === 4 && jobId && (
        <StepInterview
          jobId={jobId}
          onAllAnswered={() => setStep(5)}
        />
      )}
      {step === 5 && jobId && currentJob && (
        <StepPreview
          jobId={jobId}
          job={currentJob}
          onApply={() => handleApply.mutate()}
          applying={handleApply.isPending}
        />
      )}
      {step === 6 && currentJob && (
        <StepApply job={currentJob} />
      )}
      {step === 7 && jobId && (
        <StepReconciliation jobId={jobId} />
      )}
      {currentJob?.status === "failed" && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <strong>Import failed.</strong>{" "}
          {currentJob.completed_at && `Stopped at ${new Date(currentJob.completed_at).toLocaleString()}.`}{" "}
          Check the error in job details or start a new import.
        </Alert>
      )}

    </Box>
  );
}

// ── Step 1: Connect ───────────────────────────────────────────────────────────

function StepConnect({
  qboConn,
  connLoading,
  onConnectQbo,
  onStartQboImport,
  starting,
  onIifFile,
  onCsvFile,
}: {
  qboConn: QboConnection | null;
  connLoading: boolean;
  onConnectQbo: () => void;
  onStartQboImport: () => void;
  starting: boolean;
  onIifFile: (f: File) => void;
  onCsvFile: (f: File) => void;
}) {
  return (
    <Stack spacing={3}>
      {/* QBO card */}
      <Card variant="outlined">
        <CardHeader
          title="QuickBooks Online"
          subheader="Connect your QBO account and we'll pull everything directly"
          avatar={<LinkIcon color="primary" />}
        />
        <CardContent>
          {connLoading ? (
            <CircularProgress size={24} />
          ) : qboConn ? (
            <Stack direction="row" spacing={2} alignItems="center">
              <CheckCircleIcon color="success" />
              <Typography>
                Connected to <strong>{qboConn.company_name || qboConn.realm_id}</strong>
              </Typography>
              <Button
                variant="contained"
                onClick={onStartQboImport}
                disabled={starting}
              >
                {starting ? "Starting…" : "Start Import"}
              </Button>
            </Stack>
          ) : (
            <Button
              variant="contained"
              size="large"
              startIcon={<LinkIcon />}
              onClick={onConnectQbo}
            >
              Connect QuickBooks Online
            </Button>
          )}
        </CardContent>
      </Card>

      <Divider>or upload a file</Divider>

      {/* IIF card */}
      <Card variant="outlined">
        <CardHeader
          title="QuickBooks Desktop (IIF)"
          subheader="Export an IIF file from QuickBooks Desktop and upload it here"
          avatar={<CloudUploadIcon />}
        />
        <CardContent>
          <FileDropZone
            onFile={onIifFile}
            accept=".iif,.zip"
            label="Drop or click to upload IIF file"
            sublabel=".iif or .zip of multiple IIF files · File → Export in QuickBooks Desktop"
            disabled={starting}
          />
        </CardContent>
      </Card>

      {/* CSV card */}
      <Card variant="outlined">
        <CardHeader
          title="CSV / Excel (advanced)"
          subheader="If you have exported data in spreadsheet format"
          avatar={<CloudUploadIcon />}
        />
        <CardContent>
          <FileDropZone
            onFile={onCsvFile}
            accept=".csv,.xlsx,.zip"
            label="Drop or click to upload CSV or Excel file"
            sublabel=".csv · .xlsx · .zip of multiple files"
            disabled={starting}
          />
        </CardContent>
      </Card>
    </Stack>
  );
}

// ── Step 2: Extract ───────────────────────────────────────────────────────────

function StepExtract({ job }: { job: ImportJob }) {
  const isExtracting = ["extracting", "resolving"].includes(job.status);
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>Extracting from QuickBooks…</Typography>
        {isExtracting && <LinearProgress sx={{ mb: 2 }} />}
        {job.records_extracted != null && (
          <Stack spacing={1}>
            <Typography>
              <Chip label={job.records_extracted} size="small" color="primary" sx={{ mr: 1 }} />
              total records extracted
            </Typography>
            {job.status === "resolving" && (
              <Typography color="text.secondary" variant="body2">
                Auto-mapping in progress…
              </Typography>
            )}
          </Stack>
        )}
        {isExtracting && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This typically takes 30–90 seconds depending on your QuickBooks file size.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

// ── Step 3: Auto-map ──────────────────────────────────────────────────────────

function StepAutoMap({ job }: { job: ImportJob }) {
  const total = (job.records_auto_mapped ?? 0) + (job.records_questioned ?? 0);
  const pct = total > 0 ? Math.round(((job.records_auto_mapped ?? 0) / total) * 100) : 0;
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>Auto-mapping complete</Typography>
        <Stack spacing={2}>
          <Box>
            <LinearProgress variant="determinate" value={pct} sx={{ height: 12, borderRadius: 6 }} />
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {pct}% mapped automatically
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Chip
              label={`${job.records_auto_mapped ?? 0} auto-mapped`}
              color="success"
              variant="outlined"
            />
            <Chip
              label={`${job.records_questioned ?? 0} questions for you`}
              color="warning"
              variant="outlined"
            />
          </Stack>
          {(job.records_questioned ?? 0) > 0 && (
            <Typography color="text.secondary" variant="body2">
              Loading interview questions…
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

// ── Step 4: Interview ─────────────────────────────────────────────────────────

function StepInterview({
  jobId,
  onAllAnswered,
}: {
  jobId: string;
  onAllAnswered: () => void;
}) {
  const qc = useQueryClient();
  const [answerValue, setAnswerValue] = useState<string>("");
  const [customDate, setCustomDate] = useState<string>("");

  const { data: turns = [], isLoading } = useQuery<InterviewTurn[]>({
    queryKey: ["qbo-turns", jobId],
    queryFn: () => api(`/api/imports/quickbooks/jobs/${jobId}/turns`),
  });

  const currentTurn = turns.find((t) => !t.answered_at);
  const answeredCount = turns.filter((t) => t.answered_at).length;
  const totalTurns = turns.length;

  const answerTurn = useMutation({
    mutationFn: ({ turnNo, value }: { turnNo: number; value: unknown }) =>
      api
        .post(`/imports/quickbooks/jobs/${jobId}/turns/${turnNo}/answer`, {
          body: JSON.stringify({ selected_value: value }),
        })
        .then((r) => r.json()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["qbo-turns", jobId] });
      qc.invalidateQueries({ queryKey: ["qbo-job", jobId] });
      setAnswerValue("");
      setCustomDate("");
    },
  });

  useEffect(() => {
    if (!isLoading && turns.length > 0 && !currentTurn) {
      onAllAnswered();
    }
  }, [turns, currentTurn, isLoading, onAllAnswered]);

  if (isLoading) return <CircularProgress />;
  if (!currentTurn) return (
    <Alert severity="success">All questions answered — loading preview…</Alert>
  );

  const isCutoverDate = currentTurn.template_id === "cutover_date";
  const currentValue = isCutoverDate ? customDate : answerValue;

  const handleSubmit = () => {
    const value = isCutoverDate && answerValue === "custom" ? customDate : answerValue;
    if (!value) return;
    answerTurn.mutate({ turnNo: currentTurn.turn_no, value });
  };

  return (
    <Card variant="outlined">
      <CardContent>
        {/* Progress */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Question {answeredCount + 1} of {totalTurns}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={totalTurns > 0 ? (answeredCount / totalTurns) * 100 : 0}
            sx={{ flex: 1, height: 6, borderRadius: 3 }}
          />
        </Stack>

        <Typography variant="h6" gutterBottom>
          {currentTurn.question_text}
        </Typography>

        <FormControl fullWidth>
          <RadioGroup
            value={answerValue}
            onChange={(e) => setAnswerValue(e.target.value)}
          >
            {currentTurn.options.map((opt) => (
              <FormControlLabel
                key={opt.value}
                value={opt.value}
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">{opt.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {opt.action_descr}
                    </Typography>
                  </Box>
                }
                sx={{ mb: 1, alignItems: "flex-start" }}
              />
            ))}
          </RadioGroup>
        </FormControl>

        {/* Custom date input for Q8 */}
        {isCutoverDate && answerValue === "custom" && (
          <TextField
            label="Enter cutover date"
            type="date"
            value={customDate}
            onChange={(e) => setCustomDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 2, width: 240 }}
          />
        )}

        <Button
          variant="contained"
          sx={{ mt: 3 }}
          disabled={!answerValue || answerTurn.isPending}
          onClick={handleSubmit}
        >
          {answerTurn.isPending ? "Saving…" : "Next"}
        </Button>

        {/* Previously answered */}
        {answeredCount > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
            {answeredCount} of {totalTurns} questions answered
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

// ── Step 5: Preview ───────────────────────────────────────────────────────────

function StepPreview({
  jobId,
  job,
  onApply,
  applying,
}: {
  jobId: string;
  job: ImportJob;
  onApply: () => void;
  applying: boolean;
}) {
  const { data: preview, isLoading } = useQuery<PlanPreview>({
    queryKey: ["qbo-preview", jobId],
    queryFn: () => api(`/api/imports/quickbooks/jobs/${jobId}/preview`),
  });

  if (isLoading) return <CircularProgress />;

  const kindLabels: Record<string, string> = {
    create_account: "Accounts",
    create_party: "Customers & Vendors",
    create_journal: "Transactions (journals)",
    create_item: "Items",
    create_class: "Classes / Dimensions",
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>Review before applying</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Ledger Core will create the following records. This operation cannot be undone automatically —
          please review carefully.
        </Typography>

        <Stack spacing={1} sx={{ mb: 3 }}>
          {preview &&
            Object.entries(preview.by_kind).map(([kind, count]) => (
              <Stack key={kind} direction="row" justifyContent="space-between">
                <Typography>{kindLabels[kind] ?? kind}</Typography>
                <Chip label={count} size="small" />
              </Stack>
            ))}
          {preview && (
            <Divider />
          )}
          {preview && (
            <Stack direction="row" justifyContent="space-between">
              <Typography fontWeight={600}>Total steps</Typography>
              <Chip label={preview.total_steps} size="small" color="primary" />
            </Stack>
          )}
        </Stack>

        {job.cutover_date && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Cutover date: <strong>{job.cutover_date}</strong>. Opening balances will be posted
            on {job.cutover_date ? new Date(job.cutover_date + "T00:00:00").toLocaleDateString() : "—"}.
          </Alert>
        )}

        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={onApply}
            disabled={applying}
          >
            {applying ? "Starting apply…" : "Apply Import"}
          </Button>
          <Button variant="outlined" disabled>
            Save and resume later
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ── Step 6: Apply ─────────────────────────────────────────────────────────────

function StepApply({ job }: { job: ImportJob }) {
  const total = (job.records_auto_mapped ?? 0) + (job.records_questioned ?? 0);
  const applied = job.records_applied ?? 0;
  const pct = total > 0 ? Math.min(100, Math.round((applied / total) * 100)) : 0;

  const subSteps = [
    { label: "Chart of Accounts", done: applied > 0 },
    { label: "Customers & Vendors", done: applied > (total * 0.3) },
    { label: "Journal Entries & Transactions", done: applied > (total * 0.6) },
    { label: "Opening Balances", done: job.status === "reconciling" || job.status === "done" },
  ];

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>Applying import…</Typography>
        <LinearProgress variant="determinate" value={pct} sx={{ height: 12, borderRadius: 6, mb: 1 }} />
        <Typography variant="body2" sx={{ mb: 3 }}>
          {applied} of approximately {total} records applied
        </Typography>

        <Stack spacing={1}>
          {subSteps.map((s) => (
            <Stack key={s.label} direction="row" spacing={1} alignItems="center">
              {s.done
                ? <CheckCircleIcon fontSize="small" color="success" />
                : <CircularProgress size={16} />
              }
              <Typography variant="body2">{s.label}</Typography>
            </Stack>
          ))}
        </Stack>

        {job.status === "reconciling" && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Import complete — running trial balance check…
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

// ── Step 7: Reconciliation ────────────────────────────────────────────────────

function StepReconciliation({ jobId }: { jobId: string }) {
  const { data: rec, isLoading } = useQuery<Reconciliation>({
    queryKey: ["qbo-reconciliation", jobId],
    queryFn: () =>
      api(`/api/imports/quickbooks/jobs/${jobId}/reconciliation`),
    retry: 2,
  });

  if (isLoading) return <CircularProgress />;

  const matched = rec?.status === "matched";
  const lcOnly = rec?.status === "lc_only" || rec?.status === "skipped";
  const hasMismatches = (rec?.mismatches?.length ?? 0) > 0;

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
          {matched || lcOnly ? (
            <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
          ) : (
            <RefreshIcon color="warning" sx={{ fontSize: 40 }} />
          )}
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Import Complete
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {matched && `Trial balance matches — ${rec.accounts_matched} accounts verified.`}
              {lcOnly && "Import complete. Trial balance comparison is not available for file uploads."}
              {hasMismatches && `${rec?.mismatches?.length} account(s) have balance differences greater than $0.01.`}
            </Typography>
          </Box>
        </Stack>

        {rec && !lcOnly && (
          <Stack spacing={1} sx={{ mb: 2 }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">LC accounts</Typography>
              <Typography variant="body2" fontWeight={600}>{rec.lc_account_count ?? rec.accounts_matched ?? "—"}</Typography>
            </Stack>
            {rec.accounts_matched != null && (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">Accounts matched to QB</Typography>
                <Typography variant="body2" fontWeight={600}>{rec.accounts_matched}</Typography>
              </Stack>
            )}
          </Stack>
        )}

        {hasMismatches && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Balance differences:</Typography>
            <Stack spacing={1}>
              {rec?.mismatches?.map((m) => (
                <Stack key={m.account} direction="row" justifyContent="space-between"
                  sx={{ p: 1, bgcolor: "warning.light", borderRadius: 1 }}>
                  <Typography variant="body2">{m.account}</Typography>
                  <Typography variant="body2">
                    QB: {m.qbo_balance} · LC: {m.lc_balance} · Δ {m.delta}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}

        <Alert severity="success" sx={{ mt: 3 }}>
          Your QuickBooks data has been imported into Ledger Core. Review your Chart of Accounts
          and run a Trial Balance report to verify the opening balances.
        </Alert>
      </CardContent>
    </Card>
  );
}
